import Selection, {CLASSNAME_SPACE_APPENDED} from "./selection.js";
import {account, annotation, annotationSelection, annotationUuid, tempAnnotation, topic} from "./data/model/index.js";
import WebsiteBehaviors from "./website-behaviors/index.js";
import {TEMP_HIGHLIGHT_UUID} from "./constants/general.js";
import {getHighlightMark, getHighlightToggle, HIGHLIGHT_MARK_CLASS} from "./utils/highlight.js";

export interface pageCoords {x: number; y: number}
type highlightSelectFunc = (x: number, y: number, uuid: annotationUuid) => void;

class Renderer {
	renderedAnnotationUuids: annotationUuid[];
	tmpSelection:            annotationSelection | null;

	onHighlightSelect: highlightSelectFunc;

	//
	// PUBLIC FUNCTIONS
	//

	constructor(params?: {hooks: {onHighlightSelect: highlightSelectFunc}}) {
		if (params)
			this.onHighlightSelect = params?.hooks.onHighlightSelect;

		// internal state
		this.renderedAnnotationUuids = [];
		this.tmpSelection = null;
	}

	/**
	 * This method is called when the user clicks (or taps) on an existing annotation.
	 * It determines the uuid of the annotation and calls the onHighlightSelect callback
	 * with the uuid.
	 * Compare with handleHighlightAccessibleClick().
	 */
	handleHighlightClick = (evt: MouseEvent) => {
		const target = evt.target as HTMLElement;
		if (!target)
			return;

		const uuid = target.getAttribute("data-uuid") as annotationUuid;
		this.onHighlightSelect(evt.pageX, evt.pageY, uuid);
	};

	/**
	 * Called when user clicks the accessibility button for a highlight.
	 * Compare with handleHighlightClick().
	 */
	handleHighlightAccessibleClick = (evt: MouseEvent) => {
		const target = evt.target as HTMLElement;
		if (!target)
			return;

		const uuid = target.getAttribute("data-uuid") as annotationUuid;

		// Try to open the comments menu at the bottom right of the rendered highlight
		const highlightMark = getHighlightMark(uuid);
		const boundingRect = highlightMark
			? highlightMark.getBoundingClientRect()
			: target.getBoundingClientRect();
		this.onHighlightSelect(boundingRect.right + window.scrollX, boundingRect.bottom + window.scrollY, uuid);
	};

	/**
	 * Render all provided highlights.
	 * Returns an object indicating which annotations were rendered.
	 *
	 * @param {annotation} annotations Array of annotations to render
	 * @param {WebsiteBehaviors} websiteBehaviors
	 * @param {account} account User's account object
	 * @param {function} getTopicFromAnnotation
	 * @return {Object.<string, true>} Success result for each annotation. Keys are annotation UUIDs.
	 */
	renderHighlights(
		annotations: annotation[],
		websiteBehaviors: WebsiteBehaviors,
		account: account,
		getTopicFromAnnotation: (uuid: annotationUuid) => topic
	) {
		const success: Record<annotationUuid, boolean> = {};
		annotations.forEach(annotation => {
			const uuid = annotation.uuid;
			const topic = getTopicFromAnnotation(uuid);
			success[uuid] = this.renderHighlight(annotation, websiteBehaviors, account, topic.color);
		});

		return success;
	}

	/**
	 * Render a single highlight.
	 * Returns whether the annotation was rendered.
	 *
	 * @param {Object} annotation Annotation to render
	 * @param {WebsiteBehaviors} websiteBehaviors
	 * @param {Object} account User's account object
	 * @param {string} topicColor
	 * @return {boolean}
	 */
	renderHighlight(annotation: annotation | tempAnnotation, websiteBehaviors: WebsiteBehaviors, account: account, topicColor: string) {
		// If highlight exists in DOM, remove it first
		if (document.querySelector(".aa-highlight[data-uuid='" + annotation.uuid + "']"))
			this.removeHighlight(annotation.uuid);

		let success = false;
		const uuid = websiteBehaviors.renderHighlight(annotation, account, topicColor);
		if (uuid) {
			// Make a record of each annotation we render
			this.renderedAnnotationUuids.push(uuid);
			success = true;

			// mouse/touch events
			const hls = document.querySelectorAll(`.${HIGHLIGHT_MARK_CLASS}[data-uuid="${annotation.uuid}"]`);
			hls.forEach(hl => { // there can be more than one <mark> for each highlight
				// without "passive", this fails on touchscreen devices
				hl.addEventListener("click", this.handleHighlightClick, {passive: true});
			});

			// accessibility events
			getHighlightToggle(uuid)
				?.addEventListener("click", this.handleHighlightAccessibleClick, {passive: true});
		}

		return success;
	}

	renderTempHighlight = (websiteBehaviors: WebsiteBehaviors, accountData: account) => {
		if (!this.tmpSelection)
			return;

		const tmpAnnotation: tempAnnotation = {
			selection: this.tmpSelection,
			uuid:      TEMP_HIGHLIGHT_UUID,
			type:      "highlight"
		};

		this.renderHighlight(tmpAnnotation, websiteBehaviors, accountData, "6faffc");
	};

	/**
	 * Removes all annotations from the DOM.
	 */
	removeAllAnnotations() {
		this.renderedAnnotationUuids.forEach(this.removeHighlight.bind(this));

		if (this.renderedAnnotationUuids.length !== 0)
			console.warn("Couldn't remove all annotations from DOM");
	}

	/**
	 * Removes specified rendered highlight from DOM.
	 *
	 * @todo Probably a good idea to move this to websiteBehaviors, since the "render" function is there.
	 * @TODO: fix the annotationUuid type
	 */
	removeHighlight(annotationUuid: annotationUuid | string) {
		// First remove the keyboard toggle button
		const toggleButton = getHighlightToggle(annotationUuid);
		toggleButton?.parentNode?.removeChild(toggleButton);

		// Then remove the actual highlight
		const marks = document.querySelectorAll(`.${HIGHLIGHT_MARK_CLASS}[data-uuid="${annotationUuid}"]`);
		const parents = [];
		for (const node of Array.from(marks)) {
			const parent = node.parentNode;
			const newNode = document.createTextNode(node.textContent ?? "");

			parent!.replaceChild(newNode, node);

			parents.push(parent);
		}

		// Search each parent node's children for split text nodes and join them.
		// 1. Remove duplicate nodes.``
		const duplicateNodesIdx: number[] = [];

		for (let i = 0; i < (parents.length - 1); i++) {
			for (let j = (i + 1); j < parents.length; j++) {
				if (parents[i] === parents[j] && !duplicateNodesIdx.includes(j))
					duplicateNodesIdx.push(j);
			}
		}

		const idxToDelete = duplicateNodesIdx.reverse();

		for (const deletionIdx of idxToDelete)
			parents.splice(deletionIdx, 1);

		// 2. Join contiguous text nodes
		for (const parent of parents)
			parent!.normalize();

		// remove our record of the deleted annotation
		this.renderedAnnotationUuids = this.renderedAnnotationUuids.filter(uuid => uuid !== annotationUuid);
	}

	resetAppendedSpaces() {
		// TODO: Seems strange that this needs to be public.
		document.querySelectorAll("." + CLASSNAME_SPACE_APPENDED).forEach(this.removeAppendedSpace.bind(this));
	}

	/**
	 * Save current text selection temporarily on local cache.
	 */
	saveSelectionTemporarily() {
		const currentSelection = Selection.GetCurrentSelection();
		if (!currentSelection)
			return;

		this.tmpSelection = currentSelection;
	}

	/**
	 * If a temp selection is stored, and there is no active selection,
	 * recovers from the stored one.
	 */
	recoverLostSelection() {
		if (!this.tmpSelection)
			return;

		Selection.RecoverSavedSelection(this.tmpSelection);
		this.tmpSelection = null;
	}

	/**
	 * Clear the selection stored in the local cache.
	 */
	clearLostSelection() {
		this.tmpSelection = null;
	}

	/**
	 * Removes the temp annotation.
	 */
	removeTmpAnnotation() {
		var exists = !!getHighlightMark(TEMP_HIGHLIGHT_UUID);
		if (!exists)
			return;

		this.removeHighlight(TEMP_HIGHLIGHT_UUID);
	}

	/**
	 * Scrolls to an annotation that is rendered in the page.
	 * Returns false if the annotation was not present in the page.
	 */
	scrollToPageAnnotation(annotationUuid: annotationUuid): boolean {
		const annotation = getHighlightMark(annotationUuid);
		if (!annotation)
			return false;

		let top = (annotation as any).offsetTop - 50;
		top = top > 0 ? top : 0;

		window.scroll({
			top,
			behavior: "smooth"
		});

		return true;
	}

	//
	// PRIVATE FUNCTIONS
	//

	removeAppendedSpace(element: Element, isTopLevel = true) {
		if (element.children.length > 0) {
			// Not a leaf
			for (const childElem of Array.from(element.children))
				this.removeAppendedSpace(childElem, false);
		} else {
			(element as HTMLElement).textContent = (element as HTMLElement).textContent?.replace(/ $/, "") ?? "";
		}

		if (isTopLevel)
			element.classList.remove(CLASSNAME_SPACE_APPENDED);
	}

	/**
	 * Compares two selection objects for equality.
	 *
	 * @param   object  a  Selection object.
	 * @param   object  b  Selection object.
	 * @return  bool
	 */
	areSelectionsEqual(a: annotationSelection, b: annotationSelection) {
		if (!(a && b))
			return false;

		const test1 = a.startTreeStr === b.startTreeStr;
		const test2 = a.startOffset === b.startOffset;
		const test3 = a.endTreeStr === b.endTreeStr;
		const test4 = a.endOffset === b.endOffset;

		return test1 && test2 && test3 && test4;
	}

	/**
	 * Compares two page coordinates objects for equality.
	 *
	 * @param   object  a  Object with x,y coordinates.
	 * @param   object  b  Object with x,y coordinates.
	 * @return  bool
	 */
	arePageCoordsEqual(a: pageCoords, b: pageCoords) {
		if (!(a && b))
			return false;

		const test1 = a.x === b.x;
		const test2 = a.y === b.y;

		return test1 && test2;
	}
}

export default Renderer;
